import { Injectable, inject } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { WebsocketService } from '@iris/common/services/websockets/websocket.service';
import { FolderChangeEvent, FolderPermissionsChangeEvent } from '@iris/common/modules/dms/sandbox/actions/folders.actions';
import { Store } from '@ngrx/store';
import { IMessage } from '@stomp/stompjs';
import { FileUploadState } from '@iris/common/modules/dms/models/IrisDMSFile';
import { IrisDmsTrashBinStore } from '@iris/common/modules/dms/store/trash-bin.signal-store';
import { IrisEnvironmentService } from '@iris/common/services/environment.service';
import { IrisUserService } from '@iris/common/services/user.service';
import { IrisDmsGlobalStore } from '@iris/common/modules/dms/store/dms-global.signal-store';
import { FolderChangeActionType } from '@iris/common/modules/dms/models/folder-change-action-type';
import { IrisDmsFolderChangeEventI } from '@iris/common/modules/dms/models/dms-folder-change-event.model';

@Injectable({ providedIn: 'root' })
export class DmsWebsocketService {
  private readonly websocketService = inject(WebsocketService);
  private readonly store = inject(Store);
  private readonly envService = inject(IrisEnvironmentService);
  private readonly userService = inject(IrisUserService);

  private readonly dmsTrashBinStore = inject(IrisDmsTrashBinStore);
  private readonly dmsGlobalStore = inject(IrisDmsGlobalStore);

  readonly FOLDERS_URL = 'dms/folders';

  protected readonly foldersSubscriptions = new Map<string, Subscription>();
  protected readonly uploadsSubscriptions = new Map<string, Subscription>();

  get instanceId(): string {
    return this.envService.env.instanceId;
  }

  get userId(): number {
    return this.userService.me.id;
  }

  get instanceIdUrlPart(): string {
    return this.instanceId ? `/${this.instanceId}` : '';
  }

  public connectToUploadWs(uploadId: string): Observable<IMessage> {
    return this.websocketService.watchNotificationsWs(`/instance-broker${this.instanceIdUrlPart}/dms/upload/${uploadId}`);
  }

  public connectToFolderWs(folderId: string): Observable<IMessage> {
    return this.websocketService.watchNotificationsWs(`/instance-broker${this.instanceIdUrlPart}/dms/folders/${folderId}`);
  }

  public connectToTrashBinWs(projectId: number): Observable<IMessage> {
    return this.websocketService.watchNotificationsWs(`/instance-broker${this.instanceIdUrlPart}/dms/trash/${projectId}`);
  }

  public subscribeToFolder(sourceFolderId: string): void {
    if (!this.foldersSubscriptions.has(sourceFolderId)) {
      const subscription = this.connectToFolderWs(sourceFolderId)
        .pipe(
          map(({ body }) => JSON.parse(body) as IrisDmsFolderChangeEventI),
          filter(body =>
            !body.fileIds?.length
              && (body.user !== this.userId || body.action === FolderChangeActionType.AddFolder)
              || body.origin === 'api'
              || !!body.fileIds?.length,
          ),
          tap(({ action, fileIds  = [], folderIds = [] }) => {
            this.store.dispatch(FolderChangeEvent({ action, fileIds, folderIds, sourceFolderId }));
          }),
        ).subscribe();

      this.foldersSubscriptions.set(sourceFolderId, subscription);
    }
  }

  public prepareTrashBinSubscription(projectId: number): Observable<unknown> {
    return this.connectToTrashBinWs(projectId)
      .pipe(
        map(({ body }) => JSON.parse(body)),
        tap((body) => {
          const { action } = body;
          this.dmsTrashBinStore.trashBinChanged$(action);
        }),
      );
  }

  public subscribeToUpload(uploadId: string): void {
    if (!this.uploadsSubscriptions.has(uploadId)) {
      const subscription = this.connectToUploadWs(uploadId)
        .pipe(
          map(({ body }) => JSON.parse(body)),
          filter(body => body.user !== this.userId),
          tap((body) => {
            this.dmsGlobalStore.updateUploadingFileState(body.fileName, FileUploadState.Completed );
          }),
        )
        .subscribe();

      this.uploadsSubscriptions.set(uploadId, subscription);
    }
  }

  public connectToPermissionWs(folderId: string): Observable<IMessage> {
    return this.websocketService.watchNotificationsWs(`/instance-broker/${this.instanceId}/${this.FOLDERS_URL}/${folderId}/permissions/change`);
  }

  public preparePermissionsSubscription(folderId: string): Observable<unknown> {
    return this.connectToPermissionWs(folderId)
      .pipe(
        map(({ body }) => JSON.parse(body)),
        tap(({ permission, group }) => {
          this.store.dispatch(FolderPermissionsChangeEvent({ permission, group }));
        }),
      );
  }

  public unsubscribeFromFolder(folderId: string): void {
    if (this.foldersSubscriptions.has(folderId)) {
      this.foldersSubscriptions.get(folderId).unsubscribe();
      this.foldersSubscriptions.delete(folderId);
    }
  }
  
  public unsubscribeFromAllFolders(): void {
    this.foldersSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.foldersSubscriptions.clear();
  }

  public unsubscribeFromAllUploads(): void {
    this.uploadsSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.uploadsSubscriptions.clear();
  }
}
